import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationExtras, ParamMap, Router } from '@angular/router';
import { CoreService, DialogResult, DialogService, DspxCardSetBaseFormComponent, SpinnerService } from 'dspx-core';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from 'src/app/constants/api-endpoints.constants';
import { FILTER_QUESTION_CODES, NAV_TAB_NUMBERS, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, REFERENCE_DATA_IDS, SAVE_RESULT_STATUS, SECTION_CODES, SERVER_ERROR_POP_UP_CODES } from 'src/app/constants/app-data.constants';
import { ROUTE_CONSTANTS } from 'src/app/constants/route.constants';
import { TEMPLATES } from 'src/app/constants/templates.constants';
import { PersonGridFilterRequest } from 'src/app/interfaces/person-grid-filter-request.interface';
import { UpdateCarerApplicationRequest } from 'src/app/models/update-carer-application-request';
import { AppDataService } from 'src/app/services/app-data.service';
import { ErrorService } from 'src/app/services/error.service';
import { ResponseParserService } from 'src/app/services/response-parser.service';
import { Mapper, MapperBuilder } from '../../helpers/mapper';
import { UpdatePlacementApplicationRequest } from '../../models/update-placement-application-request';
import { BaseCardSetListComponent } from '../base-card-set-list-component/base-card-set-list.component';

@Component({
  selector: 'app-person-list',
  templateUrl: './person-list.component.html',
  styleUrl: './person-list.component.scss'
})
export class PersonListComponent extends BaseCardSetListComponent implements OnInit, OnDestroy {

  @ViewChild(DspxCardSetBaseFormComponent)
  private set _baseForm(value: DspxCardSetBaseFormComponent) {
    this.baseForm = value
  }

  filterQuestionToFilterModelMapper: Mapper;
  activeDialog: string = '';
  private createCarerApplicationId: string;
  private createPlacementApplicationId: string;
  private localAdoptionPersonId: string = '';
  private localAdoptionRoleId: string = '';
  private localAdoptionIsFromClient: boolean;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected spinnerService: SpinnerService,
    protected dialogService: DialogService,
    protected responseParserService: ResponseParserService,
    protected appDataService: AppDataService,
    private coreService: CoreService,
    private errorService: ErrorService,
  ) {
    super(
      router,
      spinnerService,
      dialogService,
      responseParserService,
      appDataService,
      TEMPLATES.personList,
      () => {
        return this.createCarerApplicationId
          ? API_ENDPOINTS.dashboard.getPersonListForCarerApplicationUpdate
          : this.createPlacementApplicationId
            ? API_ENDPOINTS.dashboard.getPersonListForPlacementApplicationUpdate
            : this.localAdoptionPersonId && this.localAdoptionIsFromClient
              ? API_ENDPOINTS.dashboard.getPersonListForLocalAdoptionMatchCarer
              : this.localAdoptionPersonId && !this.localAdoptionIsFromClient
                ? API_ENDPOINTS.dashboard.getPersonListForLocalAdoptionMatchClient
                : API_ENDPOINTS.dashboard.getPersons
      },
      false);

    this.filterQuestionToFilterModelMapper =
      new MapperBuilder()
        .WithDefaultTargetFieldMapping(/Filter$/i, "") // When target field name is not specified, just use the source field's name, but drop "Filter" from the end
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personRoleFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personEppCustomerIdFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personFirstNameFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personLastNameFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personGenderFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personDateOfBirthFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personEmailFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personPhoneFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personResidentialAddressFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personResidentialAddressPostcodeFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personResidentialAddressCityFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personResidentialAddressStateFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personResidentialAddressCountryFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personPostalAddressFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personPostalAddressCityFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personPostalAddressStateFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personPostalAddressPostcodeFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personPostalAddressCountryFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personCrisIdFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.personCrisspIdFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.vendorNameFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.vendorNumberFilter)
        .MapFrom(FILTER_QUESTION_CODES.personListForm.vendorSiteAddressFilter)
        .BuildMapper();
  }

  ngOnInit(): void {
    const mappedUrl = this.route.snapshot.url.map(segment => segment.path);

    this.formServiceEndpoint = API_ENDPOINT_KEYS.getPersonListConfig;

    if (mappedUrl.includes(ROUTE_CONSTANTS.person.updateCreateCarerApplicationPrefix)) {
      this.registerSubscription(
        this.route.paramMap.subscribe((paramMap: ParamMap) => {
          const id = paramMap.get('id');
          if (id) {
            this.createCarerApplicationId = id;
          }
        })
      );
      this.formServiceEndpoint = API_ENDPOINT_KEYS.getPersonListConfigForCarerApplicationUpdate;
    }

    if (mappedUrl.includes(ROUTE_CONSTANTS.person.updateCreatePlacementApplicationPrefix)) {
      this.registerSubscription(
        this.route.paramMap.subscribe((paramMap: ParamMap) => {
          const id = paramMap.get('id');
          if (id) {
            this.createPlacementApplicationId = id;
          }
        })
      );
      this.formServiceEndpoint = API_ENDPOINT_KEYS.getPersonListConfigForPlacementApplicationUpdate;
    }

    if (mappedUrl.includes(ROUTE_CONSTANTS.person.updateCreateLocalAdoptionPlacementPrefix)) {
      this.registerSubscription(
        this.route.paramMap.subscribe((paramMap: ParamMap) => {
          this.localAdoptionPersonId = paramMap.get('personId');
          this.localAdoptionRoleId = paramMap.get('roleId');
          const localAdoptionIsFromClientParameter = paramMap.get('isFromClient');
          if (localAdoptionIsFromClientParameter) {
            this.localAdoptionIsFromClient = localAdoptionIsFromClientParameter.toLowerCase() === 'true';
          }
        })
      );
    }

    this.coreService.applicationFunctions = {
      exit: () => this.navigateBack(),
      showBackButton: () => this.showBackButton(),
    };

    this.coreService.conditionFunctions = {
      showBackButton: () => this.showBackButton(),
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoad(event: any): void {
    if (history.state.personFilterRequest == null) {
      this.loadGridDataOnFormLoad();
    }
    else {
      this.loadGridDataOnFormLoad(history.state.personFilterRequest);
    }
  }

  filterData(event: any): void {
    switch (event.field.key) {
      case QUESTION_CODES.personListForm.personListFilter:
        {
          const personFilterRequest = this.getPersonFilterRequest();
          this.loadGridData(personFilterRequest);
          break;
        }
    }
  }

  actionButton_personGrid_datagrid_new_Clicked(event: any): void {
    const navExtras = this.getNavigationExtras();
    this.router.navigate(['/', ROUTE_CONSTANTS.person.careAllowancePerson.prefix], navExtras);
  }

  actionButton_personGrid_datagrid_edit_Clicked(event: any): void {
    this.router.navigate(['/', ROUTE_CONSTANTS.person.careAllowancePerson.prefix, event.event.data.id]);
  }

  actionButton_personGrid_datagrid_view_Clicked(event: any): void {
    const navExtras = this.getNavigationExtras();
    let routerCommands: string[] =
      this.createCarerApplicationId != null
        ? ['/', ROUTE_CONSTANTS.person.prefixDetail, event.event.data.id, ROUTE_CONSTANTS.person.updateCreateCarerApplicationPrefix, this.createCarerApplicationId]
        : this.createPlacementApplicationId != null
          ? ['/', ROUTE_CONSTANTS.person.prefixDetail, event.event.data.id, ROUTE_CONSTANTS.person.updateCreatePlacementApplicationPrefix, this.createPlacementApplicationId]
          : ['/', ROUTE_CONSTANTS.person.prefixDetail, event.event.data.id];
    this.router.navigate(routerCommands, navExtras);
  }

  actionButton_personGrid_datagrid_updateVendorApplication_Clicked(event: any): void {
    this.dialogService
      .showConfirm(POP_UP_CODES.personList.updateCreateCarerApplication)
      .pipe(take(1))
      .subscribe(data => {
        switch (data as DialogResult) {
          case DialogResult.ACCEPTED: {
            const personId = event.event.data.id;
            this.updateCarerApplication(personId);
            break;
          }
        }
      });
  }

  actionButton_personGrid_datagrid_matchToPerson_Clicked(event: any): void {
    this.activeDialog = '';
    if (this.localAdoptionPersonId) {
      this.processLocalAdoptionPersonSelection(event);
      return;
    }

    this.activeDialog = event.event.data.isCareAllowanceClient
      ? POP_UP_CODES.personList.updateCreatePlacementApplicationLinkClient
      : POP_UP_CODES.personList.updateCreatePlacementApplicationLinkNonClient;

    this.dialogService
      .showConfirm(this.activeDialog)
      .pipe(take(1))
      .subscribe(data => {
        switch (data as DialogResult) {
          case DialogResult.ACCEPTED: {
            const personId = event.event.data.id;
            this.updateCreatePlacementApplication(personId);
            break;
          }
        }
      });
  }

  actionButton_personGrid_datagrid_custom_newConcessionsApplication_Clicked(event: any): void {
    this.router.navigate(['/', ROUTE_CONSTANTS.concessionsApplication.prefix]);
  }

  private processLocalAdoptionPersonSelection(event: any) {
    const isPersonClient = event.event.data.isCareAllowanceClient;
    const isPersonCarer = event.event.data.isCareAllowanceCarer;
    const isVendorActive = event.event.data.isVendorActive;
    //If the request comes from the local adoption screen and a client is the initiator,
    //then a carer needs to be selected and vice versa.
    switch (this.localAdoptionRoleId) {
      case REFERENCE_DATA_IDS.careAllowanceUserRole.careAllowanceClient:
        if (!isPersonCarer) {
          this.activeDialog = POP_UP_CODES.personList.noCarerRole;
        }
        if (!isVendorActive) {
          this.activeDialog = POP_UP_CODES.personList.noOracleVendor;
        }
        break;
      case REFERENCE_DATA_IDS.careAllowanceUserRole.careAllowanceCarer:
        if (!isPersonClient) {
          this.activeDialog = POP_UP_CODES.personList.noClientRole;
        }
        break;
      default:
        return;
    }
    if (this.activeDialog) {
      this.dialogService.showDialog(this.activeDialog);
      return
    }
    this.activeDialog = POP_UP_CODES.personList.contactMatch
    this.dialogService
      .showConfirm(this.activeDialog)
      .pipe(take(1))
      .subscribe(data => {
        switch (data as DialogResult) {
          case DialogResult.ACCEPTED: {
            const associatedPersonId = event.event.data.id;
            this.router.navigate(['/',
              ROUTE_CONSTANTS.careAllowanceLocalAdoption.prefix,
              ROUTE_CONSTANTS.careAllowanceLocalAdoption.segments.person,
              this.localAdoptionPersonId,
              ROUTE_CONSTANTS.careAllowanceLocalAdoption.segments.role,
              this.localAdoptionRoleId,
              ROUTE_CONSTANTS.careAllowanceLocalAdoption.segments.associatedPerson,
              associatedPersonId]); //navigate to the local adoption screen with the associated person.
            break;
          }
        }
      });
  }

  private showBackButton(): boolean {
    return this.createCarerApplicationId != null || this.createPlacementApplicationId != null;
  }

  private navigateBack() {
    if (this.createCarerApplicationId != null) {
      this.router.navigate(['/', ROUTE_CONSTANTS.careAllowanceApplication.prefix, ROUTE_CONSTANTS.applicationDetailCreateUpdateCarer.prefix, this.createCarerApplicationId]);
    }

    if (this.createPlacementApplicationId != null) {
      this.router.navigate(['/', ROUTE_CONSTANTS.careAllowanceApplication.prefix, ROUTE_CONSTANTS.applicationDetailCreateUpdatePlacement.prefix, this.createPlacementApplicationId]);
    }
  }

  private getPersonFilterRequest(): PersonGridFilterRequest {
    const filterModel = this.getQuestionValue(
      this.model,
      NAV_TAB_NUMBERS.personListForm.persons,
      PAGE_NUMBERS.personListForm.persons,
      SECTION_CODES.personListForm.person,
      QUESTION_CODES.personListForm.personListFilter
    );

    return this.filterQuestionToFilterModelMapper.Map(filterModel);
  }

  private updateCarerApplication(personId: string) {
    const request: UpdateCarerApplicationRequest =
    {
      applicationId: this.createCarerApplicationId,
      contactId: personId
    };
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.applicationDetailCreateUpdateCarer.updateCreateCarerApplication, request)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(_ => {
          this.router.navigate(['/', ROUTE_CONSTANTS.careAllowanceApplication.prefix, ROUTE_CONSTANTS.applicationDetailCreateUpdateCarer.prefix, this.createCarerApplicationId]);
        }
        )
    );
  }

  private updateCreatePlacementApplication(personId: string) {
    const request: UpdatePlacementApplicationRequest =
    {
      applicationId: this.createPlacementApplicationId,
      personId: personId
    };
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(API_ENDPOINTS.applicationDetailCreateUpdatePlacement.updateCreatePlacementApplication, request)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            if (SERVER_ERROR_POP_UP_CODES.linkPersonToCreatePlacementApplication.includes(data.message)) {
              this.activeDialog = data.message;
              this.dialogService.showDialog(this.activeDialog);
            }
            else {
              this.router.navigate(['/', ROUTE_CONSTANTS.careAllowanceApplication.prefix, ROUTE_CONSTANTS.applicationDetailCreateUpdatePlacement.prefix, this.createPlacementApplicationId]);
            }
          }
        }
        )
    );
  }

  private getNavigationExtras(): NavigationExtras {
    const filterRequest = this.getPersonFilterRequest();

    return {
      state: {
        personFilterRequest: filterRequest
      }
    };
  }
}
